import { extractApiError, type HttpApiClient } from "@vana/core";
import { type Report, type ResolveParams } from "../../domain/entities/report";
import { ListReportsError, ResolveReportError } from "../../domain/errors/errors";
import { type ReportsRepository } from "../../domain/repos/reports-repo";
import {
  type ReportQueueResponse,
  type ReportResponse,
} from "../../infrastructure/responses/reports-responses";

export class ReportsRepo implements ReportsRepository {
  constructor(private readonly apiClient: HttpApiClient) {}

  async listQueue(supervisorId: string): Promise<Report[]> {
    try {
      // Scope the queue to the supervisor's ambassadors (users.supervisor_id).
      // Omitted, the backend returns every report; the screen is supervisor-only
      // so we always pass it. No status filter — the tabs partition client-side.
      const query = supervisorId ? `?supervisor_id=${encodeURIComponent(supervisorId)}` : "";
      const response = await this.apiClient.get<ReportQueueResponse>(
        `v1/problem-reports${query}`,
      );
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListReportsError(message, apiError);
    }
  }

  async resolve(params: ResolveParams): Promise<Report> {
    try {
      // The request interceptor snake_cases the body (resolutionCode ->
      // resolution_code); the resolving supervisor is the session user.
      const response = await this.apiClient.post<ReportResponse>(
        `v1/problem-reports/${params.problemReportId}/resolution`,
        {
          resolution: params.resolution,
          resolutionCode: params.resolutionCode,
        },
      );
      return response.data as Report;
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ResolveReportError(message, apiError);
    }
  }
}
